/* eslint-disable quotes */

import {
  DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
} from "@/utils/fmb-outstanding-broadcast";
import {
  DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT,
  DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME,
  SABIL_ESTABLISHMENT_OUTSTANDING_TEMPLATE_NAME,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
} from "@/utils/sabil-outstanding-broadcast";
import {
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT,
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME,
  MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY,
  MUTTAVATTEEN_YEARLY_OUTSTANDING_TEMPLATE_NAME,
} from "@/utils/muttavatteen-outstanding-broadcast";

export {
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
  DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
  FMB_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS,
  resolveEffectiveTenantId,
  isFmbOutstandingBroadcastEnabledForTenant,
} from "@/utils/fmb-outstanding-broadcast";
export type { FmbOutstandingBroadcastDeepLinkInput } from "@/utils/fmb-outstanding-broadcast";

export {
  DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME,
  SABIL_ESTABLISHMENT_OUTSTANDING_TEMPLATE_NAME,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
  DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT,
  SABIL_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS,
  isSabilOutstandingBroadcastEnabledForTenant,
} from "@/utils/sabil-outstanding-broadcast";

export {
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME,
  MUTTAVATTEEN_YEARLY_OUTSTANDING_TEMPLATE_NAME,
  MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY,
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT,
  MUTTAVATTEEN_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS,
  isMuttavatteenOutstandingBroadcastEnabledForTenant,
} from "@/utils/muttavatteen-outstanding-broadcast";

import {
  isMiqaatRsvpFilterEnvelope,
  transformMiqaatRsvpFilterCriteria,
  type MiqaatRsvpFilterEnvelope,
} from "./miqaat-rsvp-filter-criteria";

export type BroadcastParameter =
  | string
  | {
      type?: string;
      value?: unknown;
      column?: unknown;
      columnSource?: string;
    };

export type ParsedBroadcastCsv = {
  headers: string[];
  rows: Record<string, string>[];
  itsHeader: string | null;
};

export type TemplateParamValidation = {
  isValid: boolean;
  missingParams: number[];
  errors: Record<string, string>;
};

export const extractTemplateVariables = (bodyText: string | undefined | null): number[] => {
  if (!bodyText) return [];
  const variableMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
  const variables = variableMatches.map((m) => {
    const num = parseInt(m.replace(/\{\{|\}\}/g, ""), 10);
    return num;
  });
  return [...new Set(variables)].sort((a, b) => a - b);
};

const ITS_HEADER_ALIASES = new Set(["its", "its_id", "itsid", "its_no", "itsno"]);

const normalizeHeaderKey = (h: string) => String(h).replace(/[\s_]/g, "").toLowerCase();

export const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === "," && !inQuotes) {
      result.push(cur.trim());
      cur = "";
    } else {
      cur += c;
    }
  }
  result.push(cur.trim());
  return result.map((cell) => cell.replace(/^"|"$/g, "").trim());
};

export const resolveItsColumnHeader = (headers: string[]): string | null => {
  if (!headers || headers.length === 0) return null;
  if (headers.length === 1) return headers[0];
  const found = headers.find((h) => ITS_HEADER_ALIASES.has(normalizeHeaderKey(h)));
  return found || null;
};

export const normalizeItsIdFromCell = (cell: string | undefined | null): string => {
  if (cell === undefined || cell === null) return "";
  const digits = String(cell).replace(/\D/g, "");
  return /^\d{5,}$/.test(digits) ? digits : "";
};

export const parseBroadcastCsv = (text: string): ParsedBroadcastCsv => {
  const lines = String(text)
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter((l) => l.trim() !== "");

  if (lines.length === 0) {
    return { headers: [], rows: [], itsHeader: null };
  }

  const firstCells = parseCsvLine(lines[0]);
  const singleCellNumeric =
    firstCells.length === 1 && /^\d{5,}$/.test(firstCells[0].replace(/\s/g, ""));

  let headers: string[];
  let dataStart: number;

  if (firstCells.length === 1 && singleCellNumeric) {
    headers = ["ITS"];
    dataStart = 0;
  } else {
    headers = firstCells.map((h) => String(h).trim());
    dataStart = 1;
  }

  const rows: Record<string, string>[] = [];
  for (let i = dataStart; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = cells[idx] !== undefined ? String(cells[idx]).trim() : "";
    });
    rows.push(row);
  }

  const itsHeader = resolveItsColumnHeader(headers);
  return { headers, rows, itsHeader };
};

export const extractItsIdsFromParsedCsv = (parsed: ParsedBroadcastCsv): string[] => {
  const { rows, itsHeader } = parsed;
  if (!itsHeader || !rows.length) return [];
  const ids = rows.map((r) => normalizeItsIdFromCell(r[itsHeader])).filter((id) => id.length > 0);
  return [...new Set(ids)];
};

export const validateTemplateParameters = (
  templateBodyText: string,
  parameters: Record<string, BroadcastParameter> | undefined | null,
  csvColumnHeaders: string[] = []
): TemplateParamValidation => {
  const expectedVariables = extractTemplateVariables(templateBodyText);

  if (expectedVariables.length === 0) {
    return {
      isValid: true,
      missingParams: [],
      errors: {},
    };
  }

  if (!parameters || typeof parameters !== "object") {
    return {
      isValid: false,
      missingParams: expectedVariables,
      errors: expectedVariables.reduce<Record<string, string>>((acc, varNum) => {
        acc[`parameters.${varNum}`] = `Parameter {{${varNum}}} is required`;
        return acc;
      }, {}),
    };
  }

  const missingParams = expectedVariables.filter((varNum) => {
    const paramKey = String(varNum);
    const param = parameters[paramKey] ?? parameters[varNum];

    if (param && typeof param === "object") {
      if (param.type === "text") {
        const { value } = param;
        return (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        );
      }
      if (param.type === "column") {
        const col =
          param.column === undefined || param.column === null ? "" : String(param.column).trim();
        if (!col) return true;
        const fromCsv = param.columnSource === "csv";
        if (fromCsv) {
          if (!csvColumnHeaders || csvColumnHeaders.length === 0) return true;
          return !csvColumnHeaders.includes(col);
        }
        return false;
      }
      return true;
    }

    if (typeof param === "string") {
      return param.trim() === "";
    }

    return true;
  });

  const errors = missingParams.reduce<Record<string, string>>((acc, varNum) => {
    const paramKey = String(varNum);
    const param = parameters[paramKey] ?? parameters[varNum];

    if (param && typeof param === "object") {
      if (param.type === "text") {
        acc[`parameters.${varNum}.value`] = `Parameter {{${varNum}}} value is required`;
      } else if (param.type === "column") {
        const fromCsv = param.columnSource === "csv";
        const col =
          param.column === undefined || param.column === null ? "" : String(param.column).trim();
        if (fromCsv && (!csvColumnHeaders || csvColumnHeaders.length === 0)) {
          acc[`parameters.${varNum}.column`] =
            `Parameter {{${varNum}}}: upload a CSV with column headers on the recipients step to use CSV columns`;
        } else if (
          fromCsv &&
          col &&
          csvColumnHeaders &&
          csvColumnHeaders.length > 0 &&
          !csvColumnHeaders.includes(col)
        ) {
          acc[`parameters.${varNum}.column`] =
            `Parameter {{${varNum}}}: "${col}" is not a column in the uploaded CSV`;
        } else {
          acc[`parameters.${varNum}.column`] = `Parameter {{${varNum}}} column is required`;
        }
      } else {
        acc[`parameters.${varNum}`] = `Parameter {{${varNum}}} is required`;
      }
    } else {
      acc[`parameters.${varNum}`] = `Parameter {{${varNum}}} is required`;
    }
    return acc;
  }, {});

  return {
    isValid: missingParams.length === 0,
    missingParams,
    errors,
  };
};

export const getMissingParametersMessage = (missingParams: number[]): string => {
  if (!missingParams || missingParams.length === 0) {
    return "";
  }
  return `Please fill all required parameters: ${missingParams
    .map((num) => `{{${num}}}`)
    .join(", ")}`;
};

export const transformParameters = (
  parameters: Record<string, BroadcastParameter> | undefined | null
): Record<
  string,
  { type: "text"; value: string } | { type: "column"; column: string; columnSource?: "csv" }
> => {
  const transformed: Record<
    string,
    { type: "text"; value: string } | { type: "column"; column: string; columnSource?: "csv" }
  > = {};
  if (parameters && typeof parameters === "object") {
    Object.keys(parameters).forEach((key) => {
      const param = parameters[key];

      if (param && typeof param === "object" && param.type) {
        if (param.type === "text") {
          const { value } = param;
          if (value !== undefined && value !== null && value !== "") {
            transformed[key] = {
              type: "text",
              value: String(value).trim(),
            };
          }
        } else if (param.type === "column") {
          const { column, columnSource } = param;
          if (column !== undefined && column !== null && column !== "") {
            const entry: { type: "column"; column: string; columnSource?: "csv" } = {
              type: "column",
              column: String(column).trim(),
            };
            if (columnSource === "csv") {
              entry.columnSource = "csv";
            }
            transformed[key] = entry;
          }
        }
      } else if (param !== undefined && param !== null && param !== "") {
        transformed[key] = {
          type: "text",
          value: String(param).trim(),
        };
      }
    });
  }
  return transformed;
};

export const transformRecipientPhoneNumbers = (data: {
  selectedRecipientPhones?: unknown;
}): string[] | null => {
  if (
    !data.selectedRecipientPhones ||
    !Array.isArray(data.selectedRecipientPhones) ||
    data.selectedRecipientPhones.length === 0
  ) {
    return null;
  }

  const cleaned = data.selectedRecipientPhones.filter((p) => p && String(p).trim().length > 0);

  return cleaned.length > 0 ? cleaned.map(String) : null;
};

export type FilterGroupCriteriaShape = {
  id?: string;
  logic?: "AND" | "OR" | string;
  rules?: unknown[];
  groups?: unknown[];
  hijriYearStart?: number;
  minOutstandingPercent?: number;
  thaliTypeIds?: string[];
  deliveryScheduleProfileIds?: string[];
  notifyMode?: string;
};

export type FilterCriteriaShape = FilterGroupCriteriaShape | MiqaatRsvpFilterEnvelope;

export const transformFilterCriteria = (filterCriteria: FilterCriteriaShape | null | undefined) => {
  if (!filterCriteria) {
    return null;
  }

  if (isMiqaatRsvpFilterEnvelope(filterCriteria)) {
    return transformMiqaatRsvpFilterCriteria(filterCriteria);
  }

  const group = filterCriteria as FilterGroupCriteriaShape;
  const hasRules = group.rules && group.rules.length > 0;
  const hasGroups = group.groups && group.groups.length > 0;

  if (hasRules || hasGroups) {
    return group;
  }

  return null;
};

export type BroadcastKind =
  | "TEMPLATE"
  | "MIQAAT_RSVP"
  | "EVENT_RSVP"
  | "FMB_OUTSTANDING"
  | "SABIL_OUTSTANDING"
  | "MUTTAVATTEEN_OUTSTANDING";

/**
 * UI-only tab identities for the mode selector. Chula/Establishment both persist as
 * `broadcastKind: "SABIL_OUTSTANDING"` (and monthly/yearly as `"MUTTAVATTEEN_OUTSTANDING"`) — the
 * tab only decides the `sabilType` field. Kept as separate tabs so an ITS with outstanding
 * balances in both types of a kind never gets mixed into one broadcast.
 */
export const SABIL_CHULA_OUTSTANDING_TAB = "SABIL_CHULA_OUTSTANDING";
export const SABIL_ESTABLISHMENT_OUTSTANDING_TAB = "SABIL_ESTABLISHMENT_OUTSTANDING";
export const MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB = "MUTTAVATTEEN_MONTHLY_OUTSTANDING";
export const MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB = "MUTTAVATTEEN_YEARLY_OUTSTANDING";

export type ModeTabValue =
  | BroadcastKind
  | typeof SABIL_CHULA_OUTSTANDING_TAB
  | typeof SABIL_ESTABLISHMENT_OUTSTANDING_TAB
  | typeof MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB
  | typeof MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB;

/** Which mode tab is active, given the persisted `broadcastKind`/`sabilType` and tenant gates. */
export const resolveBroadcastModeTabValue = ({
  kind,
  sabilType,
  fmbOutstandingEnabled,
  sabilOutstandingEnabled,
  muttavatteenOutstandingEnabled,
}: {
  kind: string;
  sabilType?: string | null;
  fmbOutstandingEnabled: boolean;
  sabilOutstandingEnabled: boolean;
  muttavatteenOutstandingEnabled: boolean;
}): ModeTabValue => {
  if (
    (kind === "FMB_OUTSTANDING" && !fmbOutstandingEnabled) ||
    (kind === "SABIL_OUTSTANDING" && !sabilOutstandingEnabled) ||
    (kind === "MUTTAVATTEEN_OUTSTANDING" && !muttavatteenOutstandingEnabled)
  ) {
    return "TEMPLATE";
  }
  if (kind === "SABIL_OUTSTANDING") {
    return sabilType === "ESTABLISHMENT"
      ? SABIL_ESTABLISHMENT_OUTSTANDING_TAB
      : SABIL_CHULA_OUTSTANDING_TAB;
  }
  if (kind === "MUTTAVATTEEN_OUTSTANDING") {
    return sabilType === "MUTTAVATTEEN_YEARLY"
      ? MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB
      : MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB;
  }
  return kind as BroadcastKind;
};

/**
 * Collapses the 4 outstanding sub-tabs to their parent `BroadcastKind` for the primary mode
 * selector — the primary control only decides kind; which sub-type (Chula/Establishment,
 * Monthly/Yearly) is a secondary control shown underneath, using `resolveBroadcastModeTabValue`'s
 * full `ModeTabValue` as its own value.
 */
export const derivePrimaryModeTabValue = (tabsValue: ModeTabValue): BroadcastKind => {
  if (
    tabsValue === SABIL_CHULA_OUTSTANDING_TAB ||
    tabsValue === SABIL_ESTABLISHMENT_OUTSTANDING_TAB
  ) {
    return "SABIL_OUTSTANDING";
  }
  if (
    tabsValue === MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB ||
    tabsValue === MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB
  ) {
    return "MUTTAVATTEEN_OUTSTANDING";
  }
  return tabsValue as BroadcastKind;
};

export type BroadcastCategory = "CUSTOM" | "RSVP" | "OUTSTANDING";

/** Categorizes a broadcast kind into one of 3 top-level categories for simplified mode selection. */
export const deriveBroadcastCategory = (kind: string | undefined): BroadcastCategory => {
  if (kind === "MIQAAT_RSVP" || kind === "EVENT_RSVP") {
    return "RSVP";
  }
  if (
    kind === "FMB_OUTSTANDING" ||
    kind === "SABIL_OUTSTANDING" ||
    kind === "MUTTAVATTEEN_OUTSTANDING"
  ) {
    return "OUTSTANDING";
  }
  return "CUSTOM";
};

/**
 * Default cap on rows fetched per Sabil/Mutawatteen outstanding preview. The actual send always
 * re-resolves recipients server-side from the filter criteria — this only bounds what the
 * preview table/summary fetch and display. Comfortably above any single mohalla's real
 * sabil/mutawatteen population (including household+family fan-out).
 */
export const OUTSTANDING_PREVIEW_FETCH_LIMIT = 2000;

export type SabilOutstandingTabType = "CHULA" | "ESTABLISHMENT";
export type MuttavatteenOutstandingTabType = "MUTTAVATTEEN" | "MUTTAVATTEEN_YEARLY";

export type OutstandingTabFieldUpdates = {
  broadcastKind: BroadcastKind;
  templateName: string;
  sabilType: string;
  minOutstandingPercent: number;
  notifyMode: string;
};

/** Field values the Sabil Outstanding (Chula / Establishment) tabs set on selection. */
export const getSabilOutstandingTabFieldUpdates = (
  type: SabilOutstandingTabType
): OutstandingTabFieldUpdates => ({
  broadcastKind: "SABIL_OUTSTANDING",
  templateName:
    type === "ESTABLISHMENT"
      ? SABIL_ESTABLISHMENT_OUTSTANDING_TEMPLATE_NAME
      : DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME,
  sabilType: type,
  minOutstandingPercent: DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT,
  notifyMode: SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
});

/** Field values the Mutawatteen Outstanding (Monthly / Yearly) tabs set on selection. */
export const getMuttavatteenOutstandingTabFieldUpdates = (
  type: MuttavatteenOutstandingTabType
): OutstandingTabFieldUpdates => ({
  broadcastKind: "MUTTAVATTEEN_OUTSTANDING",
  templateName:
    type === "MUTTAVATTEEN_YEARLY"
      ? MUTTAVATTEEN_YEARLY_OUTSTANDING_TEMPLATE_NAME
      : DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME,
  sabilType: type,
  minOutstandingPercent: DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT,
  notifyMode: MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY,
});

export type BroadcastFormData = {
  broadcastKind?: BroadcastKind;
  miqaatId?: string | null;
  eventId?: string | null;
  templateName?: string;
  name?: string;
  parameters?: Record<string, BroadcastParameter>;
  filterCriteria?: FilterCriteriaShape;
  selectedRecipientPhones?: string[];
  createdBy?: string;
  recipientItsIds?: string[] | null;
  recipientCsvData?: Record<string, Record<string, string>> | null;
  /** FMB_OUTSTANDING audience fields (merged into filterCriteria on create). */
  hijriYearStart?: number | null;
  minOutstandingPercent?: number | null;
  thaliTypeIds?: string[] | null;
  deliveryScheduleProfileIds?: string[] | null;
  /** Optional household / holder ITS allow-list for outstanding kinds. */
  itsIds?: string[] | null;
  /** Previewed recipients unchecked from the send, for outstanding kinds. */
  excludeItsIds?: string[] | null;
  notifyMode?: string | null;
  /**
   * SABIL_OUTSTANDING: "CHULA" | "ESTABLISHMENT". MUTTAVATTEEN_OUTSTANDING: "MUTTAVATTEEN" |
   * "MUTTAVATTEEN_YEARLY". Set by the mode tab; required for both kinds — never mixed.
   */
  sabilType?: string | null;
};

export type BroadcastCreateFormValues = BroadcastFormData & {
  recipientsPreviewed?: boolean;
  csvColumnHeaders?: string[];
};

/**
 * Client-side create validation for WhatsApp broadcasts.
 * Runs before submit so missing name / audience / params do not hit a server 400 first.
 */
export const validateBroadcastCreateForm = (
  values: BroadcastCreateFormValues
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!values.name || String(values.name).trim() === "") {
    errors.name = "Broadcast name is required";
  }

  if (!values.recipientsPreviewed) {
    errors.recipientsPreviewed =
      "Please preview recipients before saving. Click 'Preview Recipients' for filter-based selection.";
  }

  if (values.broadcastKind === "MIQAAT_RSVP") {
    if (!values.miqaatId) {
      errors.miqaatId = "Select a miqaat for this RSVP broadcast.";
    }
    return errors;
  }

  if (values.broadcastKind === "EVENT_RSVP") {
    if (!values.eventId) {
      errors.eventId = "Select an event for this RSVP broadcast.";
    }
    return errors;
  }

  if (
    values.broadcastKind === "FMB_OUTSTANDING" ||
    values.broadcastKind === "SABIL_OUTSTANDING" ||
    values.broadcastKind === "MUTTAVATTEEN_OUTSTANDING"
  ) {
    const min = values.minOutstandingPercent;
    if (min != null && (Number.isNaN(Number(min)) || Number(min) < 0 || Number(min) > 100)) {
      errors.minOutstandingPercent = "Min outstanding % must be between 0 and 100.";
    }
    return errors;
  }

  if (values.templateName && values.parameters) {
    const parameters = values.parameters || {};
    const paramKeys = Object.keys(parameters).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

    const emptyParams = paramKeys.filter((key) => {
      const param = parameters[key] as BroadcastParameter;

      if (param && typeof param === "object" && param.type) {
        if (param.type === "text") {
          const { value } = param;
          return (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "")
          );
        }
        if (param.type === "column") {
          const { column, columnSource } = param;
          const col = column === undefined || column === null ? "" : String(column).trim();
          if (!col) return true;
          if (columnSource === "csv") {
            const headers = values.csvColumnHeaders || [];
            if (!Array.isArray(headers) || headers.length === 0) return true;
            return !headers.includes(col);
          }
          return false;
        }
        return true;
      }

      return !param || param === null || (typeof param === "string" && param.trim() === "");
    });

    if (emptyParams.length > 0) {
      emptyParams.forEach((key) => {
        const param = parameters[key] as BroadcastParameter;
        if (param && typeof param === "object" && param.type) {
          if (param.type === "text") {
            errors[`parameters.${key}.value`] = `Parameter {{${key}}} value is required`;
          } else if (param.type === "column") {
            const fromCsv = param.columnSource === "csv";
            const col =
              param.column === undefined || param.column === null
                ? ""
                : String(param.column).trim();
            const headers = values.csvColumnHeaders || [];
            if (fromCsv && (!Array.isArray(headers) || headers.length === 0)) {
              errors[`parameters.${key}.column`] =
                `Parameter {{${key}}}: upload a CSV with column headers to use CSV columns`;
            } else if (
              fromCsv &&
              col &&
              Array.isArray(headers) &&
              headers.length > 0 &&
              !headers.includes(col)
            ) {
              errors[`parameters.${key}.column`] =
                `Parameter {{${key}}}: "${col}" is not a column in the uploaded CSV`;
            } else {
              errors[`parameters.${key}.column`] = `Parameter {{${key}}} column is required`;
            }
          } else {
            errors[`parameters.${key}`] = `Parameter {{${key}}} is required`;
          }
        } else {
          errors[`parameters.${key}`] = `Parameter {{${key}}} is required`;
        }
      });
    }
  }

  return errors;
};

export const DEFAULT_MIQAAT_RSVP_TEMPLATE_NAME = "miqaat_rsvp";
export const DEFAULT_EVENT_RSVP_TEMPLATE_NAME = DEFAULT_MIQAAT_RSVP_TEMPLATE_NAME;

export type FmbOutstandingFilterCriteria = {
  hijriYearStart?: number;
  minOutstandingPercent: number;
  thaliTypeIds?: string[];
  deliveryScheduleProfileIds?: string[];
  /** Optional FMB household ITS allow-list. */
  itsIds?: string[];
  notifyMode: string;
};

/** Build API filterCriteria for FMB outstanding broadcasts from form fields. */
export const buildFmbOutstandingFilterCriteria = (
  data: Pick<
    BroadcastFormData,
    | "hijriYearStart"
    | "minOutstandingPercent"
    | "thaliTypeIds"
    | "deliveryScheduleProfileIds"
    | "itsIds"
    | "notifyMode"
  >
): FmbOutstandingFilterCriteria => {
  const minRaw = data.minOutstandingPercent;
  const minOutstandingPercent =
    minRaw == null || (typeof minRaw === "string" && String(minRaw).trim() === "")
      ? DEFAULT_FMB_OUTSTANDING_MIN_PERCENT
      : Number(minRaw);
  const notifyMode =
    data.notifyMode === FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      ? FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      : FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY;

  const criteria: FmbOutstandingFilterCriteria = {
    minOutstandingPercent: Number.isFinite(minOutstandingPercent)
      ? minOutstandingPercent
      : DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
    notifyMode,
  };

  if (data.hijriYearStart != null && Number.isFinite(Number(data.hijriYearStart))) {
    criteria.hijriYearStart = Number(data.hijriYearStart);
  }

  const thaliTypeIds = (data.thaliTypeIds ?? [])
    .map(String)
    .map((id) => id.trim())
    .filter(Boolean);
  if (thaliTypeIds.length > 0) {
    criteria.thaliTypeIds = thaliTypeIds;
  }

  const deliveryScheduleProfileIds = (data.deliveryScheduleProfileIds ?? [])
    .map(String)
    .map((id) => id.trim())
    .filter(Boolean);
  if (deliveryScheduleProfileIds.length > 0) {
    criteria.deliveryScheduleProfileIds = deliveryScheduleProfileIds;
  }

  const itsIds = [
    ...new Set(
      (data.itsIds ?? [])
        .map(String)
        .map((id) => id.trim())
        .filter(Boolean)
    ),
  ];
  if (itsIds.length > 0) {
    criteria.itsIds = itsIds;
  }

  return criteria;
};

export type SabilOutstandingFilterCriteria = {
  minOutstandingPercent: number;
  itsIds?: string[];
  excludeItsIds?: string[];
  notifyMode: string;
  sabilType: string;
};

/** Build API filterCriteria for Sabil outstanding broadcasts from form fields. */
export const buildSabilOutstandingFilterCriteria = (
  data: Pick<
    BroadcastFormData,
    "minOutstandingPercent" | "itsIds" | "excludeItsIds" | "notifyMode" | "sabilType"
  >
): SabilOutstandingFilterCriteria => {
  const minRaw = data.minOutstandingPercent;
  const minOutstandingPercent =
    minRaw == null || (typeof minRaw === "string" && String(minRaw).trim() === "")
      ? DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT
      : Number(minRaw);
  const notifyMode =
    data.notifyMode === SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      ? SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      : SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY;

  const criteria: SabilOutstandingFilterCriteria = {
    minOutstandingPercent: Number.isFinite(minOutstandingPercent)
      ? minOutstandingPercent
      : DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT,
    notifyMode,
    sabilType: data.sabilType === "ESTABLISHMENT" ? "ESTABLISHMENT" : "CHULA",
  };

  const itsIds = [
    ...new Set(
      (data.itsIds ?? [])
        .map(String)
        .map((id) => id.trim())
        .filter(Boolean)
    ),
  ];
  if (itsIds.length > 0) {
    criteria.itsIds = itsIds;
  }

  const excludeItsIds = [
    ...new Set(
      (data.excludeItsIds ?? [])
        .map(String)
        .map((id) => id.trim())
        .filter(Boolean)
    ),
  ];
  if (excludeItsIds.length > 0) {
    criteria.excludeItsIds = excludeItsIds;
  }

  return criteria;
};

export type MuttavatteenOutstandingFilterCriteria = {
  minOutstandingPercent: number;
  itsIds?: string[];
  excludeItsIds?: string[];
  notifyMode: string;
  sabilType: string;
};

/** Build API filterCriteria for Mutawatteen outstanding broadcasts from form fields. */
export const buildMuttavatteenOutstandingFilterCriteria = (
  data: Pick<
    BroadcastFormData,
    "minOutstandingPercent" | "itsIds" | "excludeItsIds" | "notifyMode" | "sabilType"
  >
): MuttavatteenOutstandingFilterCriteria => {
  const minRaw = data.minOutstandingPercent;
  const minOutstandingPercent =
    minRaw == null || (typeof minRaw === "string" && String(minRaw).trim() === "")
      ? DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT
      : Number(minRaw);

  const criteria: MuttavatteenOutstandingFilterCriteria = {
    minOutstandingPercent: Number.isFinite(minOutstandingPercent)
      ? minOutstandingPercent
      : DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT,
    notifyMode: MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY,
    sabilType: data.sabilType === "MUTTAVATTEEN_YEARLY" ? "MUTTAVATTEEN_YEARLY" : "MUTTAVATTEEN",
  };

  const itsIds = [
    ...new Set(
      (data.itsIds ?? [])
        .map(String)
        .map((id) => id.trim())
        .filter(Boolean)
    ),
  ];
  if (itsIds.length > 0) {
    criteria.itsIds = itsIds;
  }

  const excludeItsIds = [
    ...new Set(
      (data.excludeItsIds ?? [])
        .map(String)
        .map((id) => id.trim())
        .filter(Boolean)
    ),
  ];
  if (excludeItsIds.length > 0) {
    criteria.excludeItsIds = excludeItsIds;
  }

  return criteria;
};

export const transformBroadcastData = (data: BroadcastFormData) => {
  const isMiqaat = data.broadcastKind === "MIQAAT_RSVP";
  const isEvent = data.broadcastKind === "EVENT_RSVP";
  const isFmbOutstanding = data.broadcastKind === "FMB_OUTSTANDING";
  const isSabilOutstanding = data.broadcastKind === "SABIL_OUTSTANDING";
  const isMuttavatteenOutstanding = data.broadcastKind === "MUTTAVATTEEN_OUTSTANDING";
  const serverResolvedAudience =
    isEvent || isFmbOutstanding || isSabilOutstanding || isMuttavatteenOutstanding;

  const kind: BroadcastKind = isMiqaat
    ? "MIQAAT_RSVP"
    : isEvent
      ? "EVENT_RSVP"
      : isFmbOutstanding
        ? "FMB_OUTSTANDING"
        : isSabilOutstanding
          ? "SABIL_OUTSTANDING"
          : isMuttavatteenOutstanding
            ? "MUTTAVATTEEN_OUTSTANDING"
            : "TEMPLATE";

  const payload: Record<string, unknown> = {
    kind,
    name: typeof data.name === "string" ? data.name.trim() : data.name,
    filterCriteria: isFmbOutstanding
      ? buildFmbOutstandingFilterCriteria(data)
      : isSabilOutstanding
        ? buildSabilOutstandingFilterCriteria(data)
        : isMuttavatteenOutstanding
          ? buildMuttavatteenOutstandingFilterCriteria(data)
          : isMiqaat
            ? transformMiqaatRsvpFilterCriteria(
                data.filterCriteria ?? {
                  itsDirectory: { include: true, filter: null },
                  muttavatteen: { include: true, filter: null },
                }
              )
            : transformFilterCriteria(data.filterCriteria),
    recipientPhoneNumbers: serverResolvedAudience ? null : transformRecipientPhoneNumbers(data),
    createdBy: data.createdBy || "admin",
  };

  if (isMiqaat) {
    payload.miqaatId = data.miqaatId;
    payload.templateName = DEFAULT_MIQAAT_RSVP_TEMPLATE_NAME;
    payload.parameters = {};
  } else if (isEvent) {
    payload.eventId = data.eventId;
    payload.templateName = DEFAULT_EVENT_RSVP_TEMPLATE_NAME;
    payload.parameters = {};
  } else if (isFmbOutstanding) {
    payload.templateName = DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME;
    payload.parameters = {};
  } else if (isSabilOutstanding) {
    // Reuse the same type->template mapping BroadcastModeFields uses on tab selection.
    payload.templateName = getSabilOutstandingTabFieldUpdates(
      data.sabilType === "ESTABLISHMENT" ? "ESTABLISHMENT" : "CHULA"
    ).templateName;
    payload.parameters = {};
  } else if (isMuttavatteenOutstanding) {
    payload.templateName = getMuttavatteenOutstandingTabFieldUpdates(
      data.sabilType === "MUTTAVATTEEN_YEARLY" ? "MUTTAVATTEEN_YEARLY" : "MUTTAVATTEEN"
    ).templateName;
    payload.parameters = {};
  } else {
    payload.templateName = data.templateName;
    payload.parameters = transformParameters(data.parameters);
  }

  if (
    Array.isArray(data.recipientItsIds) &&
    data.recipientItsIds.length > 0 &&
    !serverResolvedAudience
  ) {
    payload.recipientItsIds = data.recipientItsIds;
  }

  if (
    !serverResolvedAudience &&
    data.recipientCsvData &&
    typeof data.recipientCsvData === "object" &&
    Object.keys(data.recipientCsvData).length > 0
  ) {
    payload.recipientCsvData = data.recipientCsvData;
  }

  return payload;
};
